// ─────────────────────────────────────────────────────────────
// purchaseOrder_Add.jsx — Page 1: PR Search & Item Select
// Modern design + all original logic preserved
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  Search,
  ArrowLeft,
  Star,
  Plus,
  ClipboardList,
  Package,
  Building2,
} from "lucide-react";

const API_BASE = "http://nissi-india.com/api/purchaseOrderAdd";

const PurchaseOrderAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchIdRaw =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const storedBranch = localStorage.getItem("branch") || "";
const storedBranchType =
  localStorage.getItem("branchType") ||
  localStorage.getItem("branchtype") ||
  "";
const userId = localStorage.getItem("userId") || "";

const commonHeaders = useMemo(
  () => ({
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchIdRaw,
    "x-branch": storedBranch,
    "x-userid": userId,
  }),
  [finYearId, finYear, storedBranchIdRaw, storedBranch, userId]
);

const typeRaw = String(storedBranchType || storedBranch || "")
  .trim()
  .toUpperCase();

const branchtype_session =
  typeRaw === "HEAD-OFFICE" ||
  typeRaw === "HEAD OFFICE" ||
  typeRaw === "HO" ||
  typeRaw === "H"
    ? "HO"
    : "BRANCH";

  const branchId = Number(storedBranchIdRaw) || 0;

  const [selbranchtype, setSelbranchtype] = useState(
    branchtype_session === "HO" ? "H" : "B"
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [supplier, setSupplier] = useState("Select");
  const [reqNo, setReqNo] = useState("");
  const [projectCode, setProjectCode] = useState("Select");
  const [store, setStore] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [showAllItems, setShowAllItems] = useState(true);

  const [supplierList, setSupplierList] = useState([]);
  const [projectCodeList, setProjectCodeList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [branchList, setBranchList] = useState([]);

  const [headerData, setHeaderData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedReqIds, setSelectedReqIds] = useState([]);
  const [selectedItemKeys, setSelectedItemKeys] = useState([]);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const toBackendDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
  };

  useEffect(() => {
    if (!branchId) return;

    axios
      .get(`${API_BASE}/init`, {
        params: { branchid: branchId, branchtype: branchtype_session },
        headers: commonHeaders,
      })
      .then((res) => {
        const d = res.data?.data || {};
        setSupplierList(d.suppliers || []);
        setProjectCodeList(d.projectCodes || []);
        setStoreList(d.stores || []);
        setBranchList(d.branches || []);
      })
      .catch((err) => {
        console.error("Dropdown load failed", err);
        toast.error("Failed to load dropdowns");
      });
  }, [branchId, branchtype_session, commonHeaders]);

  useEffect(() => {
    if (branchtype_session !== "HO") setSelbranchtype("B");
  }, [branchtype_session]);

  const handleSearch = async () => {
    if (!supplier || supplier === "Select") {
      toast.error("Please Select Supplier");
      return;
    }

    const payload = {
      branchid: branchId,
      branchtype_session,
      selbranchtype,
      reqno: reqNo.trim(),
      fromdate: toBackendDate(fromDate),
      todate: toBackendDate(toDate),
      projectcode: projectCode,
      storeid: store,
      selectedbranchid: branch,
      supplierid: supplier,
      seloption: showAllItems ? "AI" : "SWI",
    };

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/search`, payload, {
        headers: commonHeaders,
      });

      setHeaderData(res.data?.headers || []);
      setItemData(res.data?.items || []);
      setSelectedReqIds([]);
      setSelectedItemKeys([]);

      const cnt = (res.data?.headers || []).length;
      if (cnt === 0) toast.info("No purchase requests found.");
      else toast.success(`${cnt} purchase request(s) found.`);
    } catch (e) {
      console.error("Search failed", e);
      toast.error(e?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleHeader = (reqId) => {
    setSelectedReqIds((prev) => {
      const next = prev.includes(reqId)
        ? prev.filter((id) => id !== reqId)
        : [...prev, reqId];

      if (!next.includes(reqId)) {
        setSelectedItemKeys((prevKeys) =>
          prevKeys.filter((key) => {
            const item = itemData.find(
              (it) => `${it.fld_itemid}_${it.fld_purchaserequestdetid}` === key
            );
            return item && next.includes(item.fld_purchaserequestid);
          })
        );
      }

      return next;
    });
  };

  const itemKey = (it) => `${it.fld_itemid}_${it.fld_purchaserequestdetid}`;

  const toggleItem = (item) => {
    const key = itemKey(item);
    setSelectedItemKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const filteredItems = useMemo(
    () =>
      selectedReqIds.length === 0
        ? []
        : itemData.filter((it) =>
            selectedReqIds.includes(it.fld_purchaserequestid)
          ),
    [selectedReqIds, itemData]
  );

  const handleAdd = () => {
    if (selectedItemKeys.length === 0) {
      toast.warning("Please select at least one item.");
      return;
    }

    const selectedItems = itemData.filter((it) =>
      selectedItemKeys.includes(itemKey(it))
    );

    const sup = supplierList.find(
      (s) => String(s.fld_supplierid) === String(supplier)
    );

    navigate("/inventory/PurchaseOrder_EntryGST", {
      state: {
        selectedItems,
        supplierId: supplier,
        supplierName: sup?.fld_suppliername || "",
        supplierAddress: sup?.fld_address1 || "",
        branchId,
        branchtype_session,
      },
    });
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Purchase Order Add</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Purchase Order Add Listing</h1>
              <p
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Search purchase requests and select items
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <button
              onClick={() => navigate("/inventory/Vendor_Rating")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
            >
              <Star size={16} />
              View Rating
            </button>
          </div>
        </div>

        {/* Filters Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-lg font-bold">Search Filters</h3>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {/* Left */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Type</label>
                  <div className="flex gap-2">
                    {[
                      ["H", "H.O"],
                      ["B", "Branch"],
                    ].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setSelbranchtype(val)}
                       disabled={false}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
                          selbranchtype === val
                            ? `${buttonClass} ${hoverClass} text-white shadow`
                            : isDark
                            ? "bg-slate-800 text-slate-300 border border-slate-600"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        } ${
                          branchtype_session !== "HO"
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {val === "H" && <Building2 size={14} />}
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {supplierList.map((s) => (
                      <option key={s.fld_supplierid} value={s.fld_supplierid}>
                        {s.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Req No</label>
                  <input
                    type="text"
                    value={reqNo}
                    onChange={(e) => setReqNo(e.target.value)}
                    placeholder="Search by PR number..."
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Project Code</label>
                  <select
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {projectCodeList.map((p, i) => (
                      <option
                        key={p.fld_projectid ?? p.fld_contractid ?? i}
                        value={
                          p.fld_projectcode ??
                          p.fld_contractid ??
                          p.fld_projectid ??
                          "Select"
                        }
                      >
                        {p.fld_projectcode ??
                          p.fld_contractname ??
                          p.fld_projectname ??
                          "Project"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Show</label>
                  <div className="flex gap-2">
                    {[
                      [true, "All Items (AI)"],
                      [false, "SWI"],
                    ].map(([val, lbl]) => (
                      <button
                        key={lbl}
                        type="button"
                        onClick={() => setShowAllItems(val)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                          showAllItems === val
                            ? `${buttonClass} ${hoverClass} text-white shadow`
                            : isDark
                            ? "bg-slate-800 text-slate-300 border border-slate-600"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {storeList.map((s) => (
                      <option key={s.fld_storeid} value={s.fld_storeid}>
                        {s.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {branchList.map((b) => (
                      <option key={b.fld_branchid} value={b.fld_branchid}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <Search size={16} />
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>

        {/* Purchase Request List */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Purchase Request List</h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {headerData.length} records
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Req No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    Store
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                    Select
                  </th>
                </tr>
              </thead>

              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-600" : "divide-gray-200"
                }`}
              >
                {headerData.length > 0 ? (
                  headerData.map((row, idx) => {
                    const isSelected = selectedReqIds.includes(
                      row.fld_purchaserequestid
                    );
                    return (
                      <tr
                        key={row.fld_purchaserequestid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${
                          isSelected
                            ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20`
                            : ""
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {row.fld_purchaserequestno}
                          </span>
                        </td>
                        <td className="px-4 py-3">{row.fld_date}</td>
                        <td className="px-4 py-3">{row.fld_storename}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleHeader(row.fld_purchaserequestid)
                            }
                            className="w-4 h-4 rounded"
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <ClipboardList size={40} className="text-gray-400" />
                        <span>
                          No data. Use filters above and click Search.
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Details */}
        {filteredItems.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <Package size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Item Details</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {filteredItems.length} items
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Item Code
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Item Name
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      HSN
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Specification
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      UOM
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Req Qty
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      PO Qty
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Price
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Due Date
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase">
                      Remarks
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-bold uppercase">
                      Select
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-600" : "divide-gray-200"
                  }`}
                >
                  {filteredItems.map((item, idx) => {
                    const key = itemKey(item);
                    const isSelected = selectedItemKeys.includes(key);
                    return (
                      <tr
                        key={key}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${
                          isSelected
                            ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20`
                            : ""
                        }`}
                      >
                        <td className="px-3 py-3 font-medium">{idx + 1}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_itemcode}
                          </span>
                        </td>
                        <td className="px-3 py-3 font-medium">
                          {item.fld_itemname}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_itemHSNNo || item.fld_hsncode || "-"}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_specification || "-"}
                        </td>
                        <td className="px-3 py-3">{item.fld_uomname}</td>
                        <td className="px-3 py-3">{item.fld_qty}</td>
                        <td className="px-3 py-3">{item.POQty}</td>
                        <td className="px-3 py-3">
                          {item.fld_price ?? item.fld_qtyprice}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_duedate || "-"}
                        </td>
                        <td className="px-3 py-3">
                          {item.fld_remarks || "-"}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleItem(item)}
                            className="w-4 h-4 rounded"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Button */}
        <div className="flex justify-center">
          <button
            onClick={handleAdd}
            className={`inline-flex items-center gap-2 px-8 py-3 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
          >
            <Plus size={18} />
            Add to Purchase Order
          </button>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseOrderAdd;